// src/pages/Appli/AppliAdmin/ClubMembersModal.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal/Modal";

import {
  getMembersByClub,
  unlinkUserFromClub,
  updateUserRoleInClub,
} from "../../../services/clubServices";

import {
  getAllValidationBadges,
  toAbsoluteBadgeUrl,
} from "../../../services/validationBadgeServices";

import { useNotification } from "../../../components/Notification/NotificationManager";

import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

const ROLES_IN_CLUB = [
  "Dirigeant(e)",
  "Enseignant(e)",
  "Coach",
  "Arbitre",
  "Judoka(te) licencié(e) au club",
];

const PAGE_SIZE = 10;
const BADGE_THUMB = "/img/luttecontrelesderives.png";

function groupBadgesByUser(allBadges = [], userIds = new Set()) {
  const badgesMap = {};

  allBadges.forEach((badge) => {
    const normalizedUserId = String(badge?.user_id || "");

    if (!normalizedUserId || !userIds.has(normalizedUserId)) {
      return;
    }

    if (!badgesMap[normalizedUserId]) {
      badgesMap[normalizedUserId] = [];
    }

    badgesMap[normalizedUserId].push(badge);
  });

  Object.keys(badgesMap).forEach((userId) => {
    badgesMap[userId].sort(
      (firstBadge, secondBadge) =>
        new Date(secondBadge?.validation_date || 0).getTime() -
        new Date(firstBadge?.validation_date || 0).getTime(),
    );
  });

  return badgesMap;
}

function BadgeList({ badges = [] }) {
  if (!badges.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {badges.map((badge, index) => {
        const badgeUrl = toAbsoluteBadgeUrl(badge?.badge_url);

        const validationDate = badge?.validation_date
          ? new Date(badge.validation_date).toLocaleDateString("fr-FR")
          : "date inconnue";

        const isLatest = index === 0;

        return (
          <a
            key={`${badge.user_id}-${index}-${badge.badge_url}`}
            href={badgeUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={`Attestation ${
              isLatest ? "(la plus récente) " : ""
            }— validée le ${validationDate}`}
            className={`inline-block overflow-hidden rounded-md transition hover:scale-105 ${
              isLatest
                ? "ring-2 ring-indigo-500"
                : "ring-1 ring-slate-300 dark:ring-slate-600"
            }`}
          >
            <img
              src={BADGE_THUMB}
              alt="Badge numérique"
              className="h-8 w-8 object-cover"
            />
          </a>
        );
      })}
    </div>
  );
}

export default function ClubMembersModal({
  isOpen,
  onClose,

  clubId,
  clubName,

  onMembersCountChange,
}) {
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [badgesByUser, setBadgesByUser] = useState({});

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);

  const fetchMembersAndBadges = useCallback(async () => {
    if (!clubId) return;

    try {
      setLoading(true);

      const membersResponse = await getMembersByClub(clubId);

      const membersList = Array.isArray(membersResponse) ? membersResponse : [];

      setMembers(membersList);

      onMembersCountChange?.(clubId, membersList.length);

      try {
        const allBadgesResponse = await getAllValidationBadges();

        const memberIds = new Set(
          membersList.map((member) => String(member.id)),
        );

        setBadgesByUser(
          groupBadgesByUser(
            Array.isArray(allBadgesResponse) ? allBadgesResponse : [],
            memberIds,
          ),
        );
      } catch (badgeError) {
        console.warn("Chargement des badges impossible :", badgeError);

        setBadgesByUser({});
      }
    } catch (error) {
      console.error("Erreur lors du chargement des membres :", error);

      setMembers([]);
      setBadgesByUser({});

      addNotification("Erreur lors du chargement des membres", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification, clubId, onMembersCountChange]);

  useEffect(() => {
    if (isOpen) {
      fetchMembersAndBadges();
      return;
    }

    setMembers([]);
    setBadgesByUser({});
    setSearch("");
    setRoleFilter("");
    setPage(1);
  }, [isOpen, fetchMembersAndBadges]);

  const filteredMembers = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return members
      .filter((member) => {
        if (!roleFilter) return true;

        return member.role_in_club === roleFilter;
      })
      .filter((member) => {
        if (!normalizedSearch) return true;

        const searchableValue = [
          `${member?.prenom || ""} ${member?.nom || ""}`,
          member?.email,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableValue.includes(normalizedSearch);
      })
      .sort((firstMember, secondMember) =>
        `${firstMember?.prenom || ""} ${firstMember?.nom || ""}`.localeCompare(
          `${secondMember?.prenom || ""} ${secondMember?.nom || ""}`,
          "fr",
          {
            sensitivity: "base",
          },
        ),
      );
  }, [members, search, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));

  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const startIndex = (safePage - 1) * PAGE_SIZE;

    return filteredMembers.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredMembers, safePage]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const handleChangeRole = async (userId, newRole) => {
    try {
      await updateUserRoleInClub(clubId, userId, newRole);

      setMembers((previousMembers) =>
        previousMembers.map((member) =>
          member.id === userId
            ? {
                ...member,
                role_in_club: newRole,
              }
            : member,
        ),
      );

      addNotification("Rôle mis à jour", "success");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du rôle :", error);

      addNotification("Erreur lors de la mise à jour du rôle", "error");
    }
  };

  const handleUnlink = async (userId) => {
    const confirmed = window.confirm("Retirer ce membre du club ?");

    if (!confirmed) return;

    try {
      await unlinkUserFromClub(clubId, userId);

      const nextMembers = members.filter((member) => member.id !== userId);

      setMembers(nextMembers);

      setBadgesByUser((previousBadges) => {
        const nextBadges = {
          ...previousBadges,
        };

        delete nextBadges[userId];

        return nextBadges;
      });

      /*
       * Mise à jour immédiate du compteur
       * affiché dans le tableau des clubs.
       */
      onMembersCountChange?.(clubId, nextMembers.length);

      addNotification("Lien retiré", "success");
    } catch (error) {
      console.error("Erreur lors du retrait du lien :", error);

      addNotification("Erreur lors du retrait du lien", "error");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="club-members-title"
      describedById="club-members-desc"
      initialFocusSelector="#members-search"
    >
      <ModalHeader
        id="club-members-title"
        title={`Membres — ${clubName || `Club #${clubId}`}`}
        subtitle={`${members.length} utilisateur${
          members.length > 1 ? "s" : ""
        } rattaché${members.length > 1 ? "s" : ""} au club`}
        onClose={onClose}
      />

      <ModalBody id="club-members-desc">
        <div className="mb-4 grid items-center gap-3 md:grid-cols-[1fr,260px]">
          <input
            id="members-search"
            type="search"
            placeholder="Rechercher par nom ou email…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
          />

          <select
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          >
            <option value="">Tous les rôles</option>

            {ROLES_IN_CLUB.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <div className="h-[55vh] min-h-[340px] max-h-[620px] overflow-auto rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left shadow-sm dark:bg-slate-950">
                <tr>
                  <th className="min-w-[260px] px-4 py-3 font-semibold">
                    Utilisateur
                  </th>

                  <th className="min-w-[240px] px-4 py-3 font-semibold">
                    Rôle dans le club
                  </th>

                  <th className="px-4 py-3 font-semibold">Attestations</th>

                  <th className="px-4 py-3 text-right font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {loading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="h-[280px] px-4 py-10 text-center text-slate-500"
                    >
                      <div className="flex flex-col items-center justify-center gap-3">
                        <span className="h-7 w-7 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                        <span>Chargement des membres…</span>
                      </div>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="h-[280px] px-4 py-10 text-center text-slate-500"
                    >
                      Aucun membre ne correspond aux filtres.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((member) => {
                    const badges = badgesByUser[String(member.id)] || [];

                    return (
                      <tr
                        key={member.id}
                        className="transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={
                                toAbsoluteUserPhotoUrl(member.photoURL) ||
                                "/img/user_avatar.jpg"
                              }
                              alt={`${member.prenom || ""} ${member.nom || ""}`}
                              className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                            />

                            <div className="min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white">
                                {member.prenom} {member.nom}
                              </p>

                              {member.email ? (
                                <a
                                  href={`mailto:${member.email}`}
                                  className="mt-0.5 block max-w-[260px] truncate text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                                >
                                  {member.email}
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <select
                            value={member.role_in_club || ""}
                            onChange={(event) =>
                              handleChangeRole(member.id, event.target.value)
                            }
                            className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                          >
                            {!member.role_in_club ? (
                              <option value="" disabled>
                                Rôle non défini
                              </option>
                            ) : null}

                            {ROLES_IN_CLUB.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-4 py-3">
                          <BadgeList badges={badges} />
                        </td>

                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleUnlink(member.id)}
                            className="inline-flex h-10 items-center justify-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700"
                          >
                            Retirer le lien
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 ? (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
            <p className="text-slate-500 dark:text-slate-400">
              Page{" "}
              <span className="font-semibold text-slate-900 dark:text-white">
                {safePage}
              </span>{" "}
              / {totalPages}
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  setPage((previousPage) => Math.max(1, previousPage - 1))
                }
                disabled={safePage === 1}
                className="inline-flex h-10 items-center rounded-xl border border-slate-300 px-4 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:hover:bg-slate-800"
              >
                Précédent
              </button>

              <button
                type="button"
                onClick={() =>
                  setPage((previousPage) =>
                    Math.min(totalPages, previousPage + 1),
                  )
                }
                disabled={safePage === totalPages}
                className="inline-flex h-10 items-center rounded-xl border border-slate-300 px-4 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:hover:bg-slate-800"
              >
                Suivant
              </button>
            </div>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 items-center rounded-xl bg-slate-100 px-4 font-medium text-slate-800 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
        >
          Fermer
        </button>
      </ModalFooter>
    </Modal>
  );
}
